import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { SsoService, TenantDomainWritable, TenantInformation } from 'api';
import { CheckboxesState } from 'pages/Firms/pages/SingleSignOn/SingleSignOn.types';
import { Logger } from 'utilities';

const useGetTenantConfiguration = ({
  firmId,
  setCheckboxes,
  setSelectedFirmRole,
  setEmailDomains,
}: {
  firmId: number | null;
  setCheckboxes: React.Dispatch<React.SetStateAction<CheckboxesState>>;
  setSelectedFirmRole: React.Dispatch<React.SetStateAction<number | null>>;
  setEmailDomains: React.Dispatch<React.SetStateAction<TenantDomainWritable[]>>;
}) => {
  const [tenantConfiguration, setTenantConfiguration] = useState<TenantInformation | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const fetchServiceProviderMetadata = useCallback(
    async (firmId: number) => {
      try {
        const response = await SsoService.apiSsoTenantRead(firmId.toString());
        setTenantConfiguration(response);
      } catch (error) {
        Logger.error('Error fetching service provider metadata:', error);
        enqueueSnackbar('Failed to fetch service provider metadata.', { variant: 'error' });
        setTenantConfiguration(null);
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    if (firmId !== null) {
      fetchServiceProviderMetadata(firmId);
    }
  }, [firmId, fetchServiceProviderMetadata]);

  useEffect(() => {
    if (tenantConfiguration) {
      setCheckboxes({
        enforcedSso: tenantConfiguration.enforced_sso || false,
        jitProvisioningEnabled: tenantConfiguration.jit_provisioning_enabled || false,
      });
      setSelectedFirmRole(tenantConfiguration.default_firm_role as number);
      setEmailDomains(tenantConfiguration.domains as unknown as TenantDomainWritable[]);
    }
  }, [tenantConfiguration, setCheckboxes, setSelectedFirmRole, setEmailDomains]);

  return {
    tenantConfiguration,
  };
};

export default useGetTenantConfiguration;
